import type { ListScoresResponse, Trajectory } from '@mastra/core/evals';
import type { ServerDetailInfo } from '@mastra/core/mcp';
import type { RequestContext } from '@mastra/core/request-context';
import type {
  PaginationInfo,
  TraceRecord,
  GetTraceLightResponse,
  GetSpanResponse,
  ListTracesArgs,
  ListTracesResponse,
  ListTracesLightResponse,
  TraceQueryRequest,
  TraceQueryTraceResponse,
  ListBranchesArgs,
  ListBranchesResponse,
  GetBranchArgs,
  GetBranchResponse,
  // Logs
  ListLogsArgs,
  ListLogsResponse,
  // Scores (observability)
  ListScoresArgs,
  ListScoresResponse as ListScoresResponseNew,
  CreateScoreBody,
  CreateScoreResponse,
  DeleteScoresArgs,
  DeleteScoresResponse,
  GetScoreAggregateArgs,
  GetScoreAggregateResponse,
  GetScoreBreakdownArgs,
  GetScoreBreakdownResponse,
  GetScoreTimeSeriesArgs,
  GetScoreTimeSeriesResponse,
  GetScorePercentilesArgs,
  GetScorePercentilesResponse,
  // Feedback
  ListFeedbackArgs,
  CreateFeedbackBody,
  CreateFeedbackResponse,
  DeleteFeedbackArgs,
  DeleteFeedbackResponse,
  UpdateFeedbackReviewStatusArgs,
  FeedbackRecord,
  GetFeedbackAggregateArgs,
  GetFeedbackAggregateResponse,
  GetFeedbackBreakdownArgs,
  GetFeedbackBreakdownResponse,
  GetFeedbackTimeSeriesArgs,
  GetFeedbackTimeSeriesResponse,
  GetFeedbackPercentilesArgs,
  GetFeedbackPercentilesResponse,
  // Metrics OLAP
  GetMetricAggregateArgs,
  GetMetricAggregateResponse,
  GetMetricBreakdownArgs,
  GetMetricBreakdownResponse,
  GetMetricTimeSeriesArgs,
  GetMetricTimeSeriesResponse,
  GetMetricPercentilesArgs,
  GetMetricPercentilesResponse,
  // Discovery
  GetMetricNamesArgs,
  GetMetricNamesResponse,
  GetMetricLabelKeysArgs,
  GetMetricLabelKeysResponse,
  GetMetricLabelValuesArgs,
  GetMetricLabelValuesResponse,
  GetEntityTypesResponse,
  GetEntityNamesArgs,
  GetEntityNamesResponse,
  GetServiceNamesResponse,
  GetEnvironmentsResponse,
  GetTagsArgs,
  GetTagsResponse,
} from '@mastra/core/storage';
import type { WorkflowInfo } from '@mastra/core/workflows';
import {
  Agent,
  MemoryThread,
  Tool,
  Processor,
  Workflow,
  Vector,
  BaseResource,
  A2A,
  A2AV1,
  MCPTool,
  AgentBuilder,
  Conversations,
  Observability,
  StoredAgent,
  DynamicWorkflow,
  StoredPromptBlock,
  StoredMCPClient,
  StoredScorer,
  StoredSkill,
  ToolProvider,
  ProcessorProvider,
  Workspace,
  Responses,
  Channels,
  AgentController,
} from './resources';
import type {
  ListScoresBySpanParams,
  LegacyTracesPaginatedArg,
  LegacyGetTracesResponse,
} from './resources/observability';
import type {
  ListFeedbackResponse,
  ClientOptions,
  CreateMemoryThreadParams,
  CreateMemoryThreadResponse,
  GetAgentResponse,
  AgentVersionIdentifier,
  GetLogParams,
  GetLogsParams,
  GetLogsResponse,
  GetToolResponse,
  GetProcessorResponse,
  GetWorkflowResponse,
  ListWorkflowRunCountsResponse,
  SaveMessageToMemoryParams,
  SaveMessageToMemoryResponse,
  McpServerListResponse,
  McpServerToolListResponse,
  GetScorerResponse,
  ListScoresByScorerIdParams,
  ListScoresByRunIdParams,
  ListScoresByEntityIdParams,
  SaveScoreParams,
  SaveScoreResponse,
  GetMemoryConfigParams,
  GetMemoryConfigResponse,
  ListMemoryThreadMessagesResponse,
  MemorySearchResponse,
  ListAgentsModelProvidersResponse,
  ListMemoryThreadsParams,
  ListMemoryThreadsResponse,
  ListStoredAgentsParams,
  ListStoredAgentsResponse,
  CreateStoredAgentParams,
  StoredAgentResponse,
  ListDynamicWorkflowsParams,
  ListDynamicWorkflowsResponse,
  UpsertDynamicWorkflowParams,
  UpsertDynamicWorkflowResponse,
  WorkflowBuilderSettingsResponse,
  ListStoredPromptBlocksParams,
  ListStoredPromptBlocksResponse,
  CreateStoredPromptBlockParams,
  StoredPromptBlockResponse,
  ListStoredScorersParams,
  ListStoredScorersResponse,
  CreateStoredScorerParams,
  StoredScorerResponse,
  ListStoredMCPClientsParams,
  ListStoredMCPClientsResponse,
  CreateStoredMCPClientParams,
  StoredMCPClientResponse,
  ListStoredSkillsParams,
  ListStoredSkillsResponse,
  CreateStoredSkillParams,
  StoredSkillResponse,
  GetSystemPackagesResponse,
  BuilderSettingsResponse,
  BuilderAvailableModelsResponse,
  PermissionPatternsResponse,
  InfrastructureStatusResponse,
  ListBuilderRegistriesResponse,
  BuilderRegistrySearchResponse,
  BuilderRegistryPopularResponse,
  BuilderRegistryPreviewResponse,
  BuilderRegistryInstallBody,
  BuilderRegistryInstallResponse,
  ListScoresResponse as ListScoresResponseOld,
  GetObservationalMemoryParams,
  GetObservationalMemoryResponse,
  AwaitBufferStatusParams,
  AwaitBufferStatusResponse,
  GetMemoryStatusResponse,
  ListWorkspacesResponse,
  ListStoredWorkspacesParams,
  ListStoredWorkspacesResponse,
  StoredWorkspaceResponse,
  ListVectorsResponse,
  ListEmbeddersResponse,
  DatasetRecord,
  DatasetItem,
  DatasetExperiment,
  DatasetExperimentResult,
  DatasetExperimentResultRow,
  ListExperimentsParams,
  ListDatasetsParams,
  ExperimentReviewCounts,
  CreateDatasetParams,
  UpdateDatasetParams,
  AddDatasetItemParams,
  UpdateDatasetItemParams,
  BatchInsertDatasetItemsParams,
  BatchDeleteDatasetItemsParams,
  GenerateDatasetItemsParams,
  GeneratedItem,
  TriggerDatasetExperimentParams,
  UpdateDatasetExperimentParams,
  CreateDatasetExperimentParams,
  CreateDatasetExperimentResponse,
  RunExperimentItemParams,
  RunExperimentItemResponse,
  SubmitExperimentResultParams,
  FinalizeExperimentParams,
  UpdateExperimentResultParams,
  CompareExperimentsParams,
  CompareExperimentsResponse,
  DatasetItemVersionResponse,
  DatasetVersionResponse,
  ListToolProvidersResponse,
  GetProcessorProvidersResponse,
  ListBackgroundTasksParams,
  ListBackgroundTasksResponse,
  BackgroundTaskResponse,
  StreamBackgroundTasksParams,
  ListSchedulesParams,
  ListSchedulesResponse,
  ScheduleResponse,
  ListScheduleTriggersParams,
  ListScheduleTriggersResponse,
  CreateScheduleInput,
  UpdateScheduleInput,
  RunScheduleResponse,
  AgentControllerInfo,
} from './types';
import { base64RequestContext, buildTenancyQuery, parseClientRequestContext, requestContextQueryString } from './utils';
import { createSseJsonTransform } from './utils/stream-transforms';

/**
 * Provides typed access to agents, workflows, and other APIs on a running Mastra server.
 * Point `baseUrl` at your server and configure authentication when required.
 *
 * @example
 * ```typescript
 * import { MastraClient } from '@mastra/client-js';
 *
 * const client = new MastraClient({ baseUrl: 'http://localhost:4111' });
 * ```
 *
 * @see For documentation bundled with your installed package, locate
 * `@mastra/client-js/package.json` with your project's resolver or package-manager
 * tooling, then read `dist/docs/SKILL.md` from that package root and follow its
 * reference links. Use package-manager tools for virtual or archived packages.
 *
 * @see [Client documentation](https://mastra.ai/reference/client-js/mastra-client)
 * if packaged docs are unavailable.
 */
export class MastraClient extends BaseResource {
  private observability: Observability;
  public readonly conversations: Conversations;
  public readonly responses: Responses;
  public readonly channels: Channels;
  constructor(options: ClientOptions) {
    super(options);
    this.observability = new Observability(options);
    this.conversations = new Conversations(options);
    this.responses = new Responses(options);
    this.channels = new Channels(options);
  }

  /**
   * Retrieves all available agents
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing map of agent IDs to agent details
   */
  public listAgents(
    requestContext?: RequestContext | Record<string, any>,
    partial?: boolean,
  ): Promise<Record<string, GetAgentResponse>> {
    const requestContextParam = base64RequestContext(parseClientRequestContext(requestContext));

    const searchParams = new URLSearchParams();

    if (requestContextParam) {
      searchParams.set('requestContext', requestContextParam);
    }

    if (partial) {
      searchParams.set('partial', 'true');
    }

    const queryString = searchParams.toString();
    return this.request(`/agents${queryString ? `?${queryString}` : ''}`);
  }

  public listAgentsModelProviders(): Promise<ListAgentsModelProvidersResponse> {
    return this.request(`/agents/providers`);
  }

  /**
   * Gets an agent instance by ID
   * @param agentId - ID of the agent to retrieve
   * @param version - Optional version selector for stored agent overrides
   * @returns Agent instance
   */
  public getAgent(agentId: string, version?: AgentVersionIdentifier, routeOverrides?: { stream?: string }) {
    return new Agent(this.options, agentId, version, routeOverrides);
  }

  /**
   * Lists the agent controllers hosted on the connected Mastra instance.
   * @returns Promise containing one record per agent controller, carrying its id
   */
  public async listAgentControllers(): Promise<AgentControllerInfo[]> {
    const body = await this.request<{ agentControllers: AgentControllerInfo[] }>('/agent-controller');
    return body.agentControllers;
  }

  /**
   * Scopes to an agent controller hosted on the connected Mastra instance. Use
   * `getAgentController(id).session(resourceId)` to create/resume a session,
   * stream its events, and send messages.
   * @param controllerId - The id the agent controller is registered under on Mastra
   */
  public getAgentController(controllerId: string) {
    return new AgentController(this.options, controllerId);
  }

  /**
   * Lists memory threads with optional filtering by resourceId and/or metadata
   * @param params - Parameters containing optional filters, pagination options, and request context
   * @returns Promise containing paginated array of memory threads with metadata
   */
  public async listMemoryThreads(params: ListMemoryThreadsParams = {}): Promise<ListMemoryThreadsResponse> {
    const queryParams = new URLSearchParams();

    // Add resourceId if provided (backwards compatible - also add lowercase version)
    if (params.resourceId) {
      queryParams.set('resourceId', params.resourceId);
    }

    // Add metadata filter as JSON string if provided
    if (params.metadata) {
      queryParams.set('metadata', JSON.stringify(params.metadata));
    }

    if (params.agentId) queryParams.set('agentId', params.agentId);
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.perPage !== undefined) queryParams.set('perPage', params.perPage.toString());
    if (params.orderBy) {
      if (params.orderBy.field) {
        queryParams.set('orderBy[field]', params.orderBy.field);
      }
      if (params.orderBy.direction) {
        queryParams.set('orderBy[direction]', params.orderBy.direction);
      }
    }

    const queryString = queryParams.toString();
    const response: ListMemoryThreadsResponse | ListMemoryThreadsResponse['threads'] = await this.request(
      `/memory/threads${queryString ? `?${queryString}` : ''}${requestContextQueryString(params.requestContext, queryString ? '&' : '?')}`,
    );

    const actualResponse: ListMemoryThreadsResponse =
      'threads' in response
        ? response
        : {
            threads: response,
            total: response.length,
            page: params.page ?? 0,
            perPage: params.perPage ?? 100,
            hasMore: false,
          };

    return actualResponse;
  }

  /**
   * Retrieves memory config for a resource
   * @param params - Parameters containing the resource ID and optional request context
   * @returns Promise containing memory configuration
   */
  public getMemoryConfig(params: GetMemoryConfigParams): Promise<GetMemoryConfigResponse> {
    return this.request(
      `/memory/config?agentId=${params.agentId}${requestContextQueryString(params.requestContext, '&')}`,
    );
  }

  /**
   * Creates a new memory thread
   * @param params - Parameters for creating the memory thread including optional request context
   * @returns Promise containing the created memory thread
   */
  public createMemoryThread(params: CreateMemoryThreadParams): Promise<CreateMemoryThreadResponse> {
    return this.request(
      `/memory/threads?agentId=${params.agentId}${requestContextQueryString(params.requestContext, '&')}`,
      { method: 'POST', body: params },
    );
  }

  /**
   * Gets a memory thread instance by ID
   * @param threadId - ID of the memory thread to retrieve
   * @param agentId - Optional agent ID. When not provided, uses storage directly
   * @returns MemoryThread instance
   */
  public getMemoryThread({ threadId, agentId }: { threadId: string; agentId?: string }) {
    return new MemoryThread(this.options, threadId, agentId);
  }

  /**
   * Lists messages for a thread.
   * @param threadId - ID of the thread
   * @param opts - Optional parameters including agentId, networkId, and requestContext
   *   - When agentId is provided, uses the agent's memory
   *   - When networkId is provided, uses the network endpoint
   *   - When neither is provided, uses storage directly
   * @returns Promise containing the thread messages
   */
  public listThreadMessages(
    threadId: string,
    opts: {
      agentId?: string;
      networkId?: string;
      requestContext?: RequestContext | Record<string, any>;
      includeSystemReminders?: boolean;
    } = {},
  ): Promise<ListMemoryThreadMessagesResponse> {
    let url = '';
    const includeSystemRemindersQuery =
      opts.includeSystemReminders === undefined ? '' : `includeSystemReminders=${opts.includeSystemReminders}`;

    if (opts.networkId) {
      url = `/memory/network/threads/${threadId}/messages?networkId=${opts.networkId}${includeSystemRemindersQuery ? `&${includeSystemRemindersQuery}` : ''}${requestContextQueryString(opts.requestContext, includeSystemRemindersQuery ? '&' : '&')}`;
    } else if (opts.agentId) {
      url = `/memory/threads/${threadId}/messages?agentId=${opts.agentId}${includeSystemRemindersQuery ? `&${includeSystemRemindersQuery}` : ''}${requestContextQueryString(opts.requestContext, '&')}`;
    } else {
      url = `/memory/threads/${threadId}/messages${includeSystemRemindersQuery ? `?${includeSystemRemindersQuery}` : ''}${requestContextQueryString(opts.requestContext, includeSystemRemindersQuery ? '&' : '?')}`;
    }
    return this.request(url);
  }

  public deleteThread(
    threadId: string,
    opts:
      | { agentId: string; networkId?: never; requestContext?: RequestContext | Record<string, any> }
      | { networkId: string; agentId?: never; requestContext?: RequestContext | Record<string, any> },
  ): Promise<{ success: boolean; message: string }> {
    if (!opts || !!opts.agentId === !!opts.networkId) {
      throw new Error(
        'MastraClient.deleteThread() requires exactly one of agentId or networkId. ' +
          'The server cannot resolve which memory store owns the thread without one, ' +
          'and passing both is ambiguous.',
      );
    }

    const url = opts.agentId
      ? `/memory/threads/${threadId}?agentId=${opts.agentId}${requestContextQueryString(opts.requestContext, '&')}`
      : `/memory/network/threads/${threadId}?networkId=${opts.networkId}${requestContextQueryString(opts.requestContext, '&')}`;

    return this.request(url, { method: 'DELETE' });
  }

  /**
   * Saves messages to memory
   * @param params - Parameters containing messages to save and optional request context
   * @returns Promise containing the saved messages
   */
  public saveMessageToMemory(params: SaveMessageToMemoryParams): Promise<SaveMessageToMemoryResponse> {
    return this.request(
      `/memory/save-messages?agentId=${params.agentId}${requestContextQueryString(params.requestContext, '&')}`,
      {
        method: 'POST',
        body: params,
      },
    );
  }

  /**
   * Gets the status of the memory system
   * @param agentId - The agent ID
   * @param opts - Optional parameters including resourceId, threadId, and requestContext
   * @returns Promise containing memory system status including observational memory info
   */
  public getMemoryStatus(
    agentId: string,
    requestContext?: RequestContext | Record<string, any>,
    opts?: {
      resourceId?: string;
      threadId?: string;
    },
  ): Promise<GetMemoryStatusResponse> {
    const queryParams = new URLSearchParams({ agentId });
    if (opts?.resourceId) queryParams.set('resourceId', opts.resourceId);
    if (opts?.threadId) queryParams.set('threadId', opts.threadId);
    const queryString = queryParams.toString();
    return this.request(`/memory/status?${queryString}${requestContextQueryString(requestContext, '&')}`);
  }

  /**
   * Gets observational memory data for a resource or thread
   * @param params - Parameters containing agentId, resourceId, threadId, and optional request context
   * @returns Promise containing the current OM record and history
   */
  public getObservationalMemory(params: GetObservationalMemoryParams): Promise<GetObservationalMemoryResponse> {
    const queryParams = new URLSearchParams({ agentId: params.agentId });
    if (params.resourceId) queryParams.set('resourceId', params.resourceId);
    if (params.threadId) queryParams.set('threadId', params.threadId);
    if (params.from) {
      queryParams.set('from', params.from instanceof Date ? params.from.toISOString() : params.from);
    }
    if (params.to) {
      queryParams.set('to', params.to instanceof Date ? params.to.toISOString() : params.to);
    }
    if (params.offset != null) queryParams.set('offset', String(params.offset));
    if (params.limit != null) queryParams.set('limit', String(params.limit));
    const queryString = queryParams.toString();
    return this.request(
      `/memory/observational-memory?${queryString}${requestContextQueryString(params.requestContext, '&')}`,
    );
  }

  /**
   * Blocks until any in-flight observational memory buffering completes, then returns the updated record
   * @param params - Parameters containing agentId, resourceId, threadId
   * @returns Promise containing the updated OM record after buffering completes
   */
  public awaitBufferStatus(params: AwaitBufferStatusParams): Promise<AwaitBufferStatusResponse> {
    return this.request(
      `/memory/observational-memory/buffer-status${requestContextQueryString(params.requestContext)}`,
      {
        method: 'POST',
        body: {
          agentId: params.agentId,
          resourceId: params.resourceId,
          threadId: params.threadId,
        },
      },
    );
  }

  /**
   * Retrieves all available tools
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing map of tool IDs to tool details
   */
  public listTools(requestContext?: RequestContext | Record<string, any>): Promise<Record<string, GetToolResponse>> {
    const requestContextParam = base64RequestContext(parseClientRequestContext(requestContext));

    const searchParams = new URLSearchParams();

    if (requestContextParam) {
      searchParams.set('requestContext', requestContextParam);
    }

    const queryString = searchParams.toString();
    return this.request(`/tools${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Gets a tool instance by ID
   * @param toolId - ID of the tool to retrieve
   * @returns Tool instance
   */
  public getTool(toolId: string) {
    return new Tool(this.options, toolId);
  }

  /**
   * Retrieves all available processors
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing map of processor IDs to processor details
   */
  public listProcessors(
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<Record<string, GetProcessorResponse>> {
    const requestContextParam = base64RequestContext(parseClientRequestContext(requestContext));

    const searchParams = new URLSearchParams();

    if (requestContextParam) {
      searchParams.set('requestContext', requestContextParam);
    }

    const queryString = searchParams.toString();
    return this.request(`/processors${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Gets a processor instance by ID
   * @param processorId - ID of the processor to retrieve
   * @returns Processor instance
   */
  public getProcessor(processorId: string) {
    return new Processor(this.options, processorId);
  }

  /**
   * Retrieves all available workflows
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing map of workflow IDs to workflow details
   */
  public listWorkflows(
    requestContext?: RequestContext | Record<string, any>,
    partial?: boolean,
  ): Promise<Record<string, GetWorkflowResponse>> {
    const requestContextParam = base64RequestContext(parseClientRequestContext(requestContext));

    const searchParams = new URLSearchParams();

    if (requestContextParam) {
      searchParams.set('requestContext', requestContextParam);
    }

    if (partial) {
      searchParams.set('partial', 'true');
    }

    const queryString = searchParams.toString();
    return this.request(`/workflows${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Retrieves per-workflow counts of running and suspended (awaiting resume) runs
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing map of workflow IDs to run counts
   */
  public listWorkflowRunCounts(
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<ListWorkflowRunCountsResponse> {
    const requestContextParam = base64RequestContext(parseClientRequestContext(requestContext));

    const searchParams = new URLSearchParams();

    if (requestContextParam) {
      searchParams.set('requestContext', requestContextParam);
    }

    const queryString = searchParams.toString();
    return this.request(`/workflows/run-counts${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Gets a workflow instance by ID
   * @param workflowId - ID of the workflow to retrieve
   * @returns Workflow instance
   */
  public getWorkflow(workflowId: string) {
    return new Workflow(this.options, workflowId);
  }

  /**
   * Gets all available agent builder actions
   * @returns Promise containing map of action IDs to action details
   */
  public getAgentBuilderActions(): Promise<Record<string, WorkflowInfo>> {
    return this.request('/agent-builder');
  }

  /**
   * Gets an agent builder instance for executing agent-builder workflows
   * @returns AgentBuilder instance
   */
  public getAgentBuilderAction(actionId: string) {
    return new AgentBuilder(this.options, actionId);
  }

  /**
   * Gets a vector instance by name
   * @param vectorName - Name of the vector to retrieve
   * @returns Vector instance
   */
  public getVector(vectorName: string) {
    return new Vector(this.options, vectorName);
  }

  /**
   * Retrieves logs
   * @param params - Parameters for filtering logs
   * @returns Promise containing array of log messages
   */
  public listLogs(params: GetLogsParams): Promise<GetLogsResponse> {
    const { transportId, fromDate, toDate, logLevel, filters, page, perPage } = params;
    const _filters = filters ? Object.entries(filters).map(([key, value]) => `${key}:${value}`) : [];

    const searchParams = new URLSearchParams();
    if (transportId) {
      searchParams.set('transportId', transportId);
    }
    if (fromDate) {
      searchParams.set('fromDate', fromDate.toISOString());
    }
    if (toDate) {
      searchParams.set('toDate', toDate.toISOString());
    }
    if (logLevel) {
      searchParams.set('logLevel', logLevel);
    }
    if (page !== undefined) {
      searchParams.set('page', String(page));
    }
    if (perPage !== undefined) {
      searchParams.set('perPage', String(perPage));
    }
    if (_filters) {
      if (Array.isArray(_filters)) {
        for (const filter of _filters) {
          searchParams.append('filters', filter);
        }
      } else {
        searchParams.set('filters', _filters);
      }
    }

    if (searchParams.size) {
      return this.request(`/logs?${searchParams}`);
    } else {
      return this.request(`/logs`);
    }
  }

  /**
   * Gets logs for a specific run
   * @param params - Parameters containing run ID to retrieve
   * @returns Promise containing array of log messages
   */
  public getLogForRun(params: GetLogParams): Promise<GetLogsResponse> {
    const { runId, transportId, fromDate, toDate, logLevel, filters, page, perPage } = params;

    const _filters = filters ? Object.entries(filters).map(([key, value]) => `${key}:${value}`) : [];
    const searchParams = new URLSearchParams();
    if (runId) {
      searchParams.set('runId', runId);
    }
    if (transportId) {
      searchParams.set('transportId', transportId);
    }
    if (fromDate) {
      searchParams.set('fromDate', fromDate.toISOString());
    }
    if (toDate) {
      searchParams.set('toDate', toDate.toISOString());
    }
    if (logLevel) {
      searchParams.set('logLevel', logLevel);
    }
    if (page !== undefined) {
      searchParams.set('page', String(page));
    }
    if (perPage !== undefined) {
      searchParams.set('perPage', String(perPage));
    }

    if (_filters) {
      if (Array.isArray(_filters)) {
        for (const filter of _filters) {
          searchParams.append('filters', filter);
        }
      } else {
        searchParams.set('filters', _filters);
      }
    }

    if (searchParams.size) {
      return this.request(`/logs/${runId}?${searchParams}`);
    } else {
      return this.request(`/logs/${runId}`);
    }
  }

  /**
   * List of all log transports
   * @returns Promise containing list of log transports
   */
  public listLogTransports(): Promise<{ transports: string[] }> {
    return this.request('/logs/transports');
  }

  /**
   * Retrieves a list of available MCP servers.
   * @param params - Optional parameters for pagination (page, perPage, or deprecated offset, limit).
   * @returns Promise containing the list of MCP servers and pagination info.
   */
  public getMcpServers(params?: {
    page?: number;
    perPage?: number;
    /** @deprecated Use page instead */
    offset?: number;
    /** @deprecated Use perPage instead */
    limit?: number;
  }): Promise<McpServerListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.perPage !== undefined) {
      searchParams.set('perPage', String(params.perPage));
    }
    // Legacy support: also send limit/offset if provided (for older servers)
    if (params?.limit !== undefined) {
      searchParams.set('limit', String(params.limit));
    }
    if (params?.offset !== undefined) {
      searchParams.set('offset', String(params.offset));
    }
    const queryString = searchParams.toString();
    return this.request(`/mcp/v0/servers${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Retrieves detailed information for a specific MCP server.
   * @param serverId - The ID of the MCP server to retrieve.
   * @param params - Optional parameters, e.g., specific version.
   * @returns Promise containing the detailed MCP server information.
   */
  public getMcpServerDetails(serverId: string, params?: { version?: string }): Promise<ServerDetailInfo> {
    const searchParams = new URLSearchParams();
    if (params?.version) {
      searchParams.set('version', params.version);
    }
    const queryString = searchParams.toString();
    return this.request(`/mcp/v0/servers/${serverId}${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Retrieves a list of tools for a specific MCP server.
   * @param serverId - The ID of the MCP server.
   * @returns Promise containing the list of tools.
   */
  public getMcpServerTools(serverId: string): Promise<McpServerToolListResponse> {
    return this.request(`/mcp/${serverId}/tools`);
  }

  /**
   * Gets an MCPTool resource instance for a specific tool on an MCP server.
   * This instance can then be used to fetch details or execute the tool.
   * @param serverId - The ID of the MCP server.
   * @param toolId - The ID of the tool.
   * @returns MCPTool instance.
   */
  public getMcpServerTool(serverId: string, toolId: string): MCPTool {
    return new MCPTool(this.options, serverId, toolId);
  }

  /**
   * Lists resources available on an MCP server.
   * @param serverId - The ID of the MCP server.
   * @returns Promise containing the list of resources.
   */
  public getMcpServerResources(serverId: string): Promise<{
    resources: Array<{
      uri: string;
      name: string;
      description?: string;
      mimeType?: string;
      _meta?: Record<string, unknown>;
    }>;
  }> {
    return this.request(`/mcp/${encodeURIComponent(serverId)}/resources`);
  }

  /**
   * Reads the content of a resource from an MCP server.
   * Used for fetching ui:// MCP App HTML content.
   * @param serverId - The ID of the MCP server.
   * @param uri - The resource URI to read.
   * @returns Promise containing the resource content.
   */
  public readMcpServerResource(
    serverId: string,
    uri: string,
  ): Promise<{ contents: Array<{ uri: string; text?: string; blob?: string }> }> {
    return this.request(`/mcp/${encodeURIComponent(serverId)}/resources/read`, {
      method: 'POST',
      body: { uri },
    });
  }

  /**
   * Gets an A2A client for interacting with an agent via the A2A protocol
   * @param agentId - ID of the agent to interact with
   * @returns A2A client instance
   */
  public getA2A(agentId: string) {
    return new A2A(this.options, agentId);
  }

  /**
   * Gets an A2A Protocol v1.0 client for an agent.
   * @param agentId - ID of the agent to interact with
   */
  public getA2AV1(agentId: string) {
    return new A2AV1(this.options, agentId);
  }

  /**
   * Retrieves the working memory for a specific thread (optionally resource-scoped).
   * @param agentId - ID of the agent.
   * @param threadId - ID of the thread.
   * @param resourceId - Optional ID of the resource.
   * @returns Working memory for the specified thread or resource.
   */
  public getWorkingMemory({
    agentId,
    threadId,
    resourceId,
    requestContext,
  }: {
    agentId: string;
    threadId: string;
    resourceId?: string;
    requestContext?: RequestContext | Record<string, any>;
  }) {
    return this.request(
      `/memory/threads/${threadId}/working-memory?agentId=${agentId}&resourceId=${resourceId}${requestContextQueryString(requestContext, '&')}`,
    );
  }

  public searchMemory({
    agentId,
    resourceId,
    threadId,
    searchQuery,
    memoryConfig,
    requestContext,
  }: {
    agentId: string;
    resourceId: string;
    threadId?: string;
    searchQuery: string;
    memoryConfig?: any;
    requestContext?: RequestContext | Record<string, any>;
  }): Promise<MemorySearchResponse> {
    const params = new URLSearchParams({
      searchQuery,
      resourceId,
      agentId,
    });

    if (threadId) {
      params.append('threadId', threadId);
    }

    if (memoryConfig) {
      params.append('memoryConfig', JSON.stringify(memoryConfig));
    }

    return this.request(`/memory/search?${params}${requestContextQueryString(requestContext, '&')}`);
  }

  /**
   * Updates the working memory for a specific thread (optionally resource-scoped).
   * @param agentId - ID of the agent.
   * @param threadId - ID of the thread.
   * @param workingMemory - The new working memory content.
   * @param resourceId - Optional ID of the resource.
   */
  public updateWorkingMemory({
    agentId,
    threadId,
    workingMemory,
    resourceId,
    requestContext,
  }: {
    agentId: string;
    threadId: string;
    workingMemory: string;
    resourceId?: string;
    requestContext?: RequestContext | Record<string, any>;
  }) {
    return this.request(
      `/memory/threads/${threadId}/working-memory?agentId=${agentId}${requestContextQueryString(requestContext, '&')}`,
      {
        method: 'POST',
        body: {
          workingMemory,
          resourceId,
        },
      },
    );
  }

  /**
   * Retrieves all available scorers
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing list of available scorers
   */
  public listScorers(
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<Record<string, GetScorerResponse>> {
    return this.request(`/scores/scorers${requestContextQueryString(requestContext)}`);
  }

  /**
   * Retrieves a scorer by ID
   * @param scorerId - ID of the scorer to retrieve
   * @returns Promise containing the scorer
   */
  public getScorer(scorerId: string): Promise<GetScorerResponse> {
    return this.request(`/scores/scorers/${encodeURIComponent(scorerId)}`);
  }

  public listScoresByScorerId(params: ListScoresByScorerIdParams): Promise<ListScoresResponseOld> {
    const { page, perPage, scorerId, entityId, entityType } = params;
    const searchParams = new URLSearchParams();

    if (entityId) {
      searchParams.set('entityId', entityId);
    }
    if (entityType) {
      searchParams.set('entityType', entityType);
    }

    if (page !== undefined) {
      searchParams.set('page', String(page));
    }
    if (perPage !== undefined) {
      searchParams.set('perPage', String(perPage));
    }
    const queryString = searchParams.toString();
    return this.request(`/scores/scorer/${encodeURIComponent(scorerId)}${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Retrieves scores by run ID
   * @param params - Parameters containing run ID and pagination options
   * @returns Promise containing scores and pagination info
   */
  public listScoresByRunId(params: ListScoresByRunIdParams): Promise<ListScoresResponseOld> {
    const { runId, page, perPage } = params;
    const searchParams = new URLSearchParams();

    if (page !== undefined) {
      searchParams.set('page', String(page));
    }
    if (perPage !== undefined) {
      searchParams.set('perPage', String(perPage));
    }

    const queryString = searchParams.toString();
    return this.request(`/scores/run/${encodeURIComponent(runId)}${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Retrieves scores by entity ID and type
   * @param params - Parameters containing entity ID, type, and pagination options
   * @returns Promise containing scores and pagination info
   */
  public listScoresByEntityId(params: ListScoresByEntityIdParams): Promise<ListScoresResponseOld> {
    const { entityId, entityType, page, perPage } = params;
    const searchParams = new URLSearchParams();

    if (page !== undefined) {
      searchParams.set('page', String(page));
    }
    if (perPage !== undefined) {
      searchParams.set('perPage', String(perPage));
    }

    const queryString = searchParams.toString();
    return this.request(
      `/scores/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${queryString ? `?${queryString}` : ''}`,
    );
  }

  /**
   * Saves a score
   * @param params - Parameters containing the score data to save
   * @returns Promise containing the saved score
   */
  public saveScore(params: SaveScoreParams): Promise<SaveScoreResponse> {
    return this.request('/scores', {
      method: 'POST',
      body: params,
    });
  }

  /** Retrieves a specific trace by ID. */
  getTrace(traceId: string): Promise<TraceRecord> {
    return this.observability.getTrace(traceId);
  }

  /** Retrieves a lightweight trace by ID (timeline fields only, excludes heavy fields). */
  getTraceLight(traceId: string): Promise<GetTraceLightResponse> {
    return this.observability.getTraceLight(traceId);
  }

  /** Retrieves a single span with full details by trace ID and span ID. */
  getSpan(traceId: string, spanId: string): Promise<GetSpanResponse> {
    return this.observability.getSpan(traceId, spanId);
  }

  /** Extracts a structured trajectory from a trace's spans. */
  getTraceTrajectory(traceId: string): Promise<Trajectory> {
    return this.observability.getTraceTrajectory(traceId);
  }

  /**
   * Retrieves paginated list of traces with optional filtering.
   * This is the legacy API preserved for backward compatibility.
   *
   * @param params - Parameters for pagination and filtering (legacy format)
   * @returns Promise containing paginated traces and pagination info
   * @deprecated Use {@link listTraces} instead for new features like ordering and more filters.
   */
  getTraces(params: LegacyTracesPaginatedArg): Promise<LegacyGetTracesResponse> {
    return this.observability.getTraces(params);
  }

  /**
   * Retrieves paginated list of traces with optional filtering and sorting.
   * This is the new API with improved filtering options.
   *
   * @param params - Parameters for pagination, filtering, and ordering
   * @returns Promise containing paginated traces and pagination info
   */
  listTraces(params: ListTracesArgs = {}): Promise<ListTracesResponse> {
    return this.observability.listTraces(params);
  }

  /** Queries completed logical traces using recursive trace and related-record predicates. */
  queryTraces(params: Omit<TraceQueryRequest, 'group'>): Promise<TraceQueryTraceResponse> {
    return this.observability.queryTraces(params);
  }

  /**
   * Retrieves paginated list of traces carrying only the fields a trace list renders.
   * Same contract as {@link listTraces}, but rows omit the `attributes`/`input`/`output`
   * blobs and carry a short `inputPreview` instead.
   *
   * @param params - Parameters for pagination, filtering, and ordering
   * @returns Promise containing paginated lightweight traces and pagination info
   */
  listTracesLight(params: ListTracesArgs = {}): Promise<ListTracesLightResponse> {
    return this.observability.listTracesLight(params);
  }

  /**
   * Retrieves a paginated list of trace branches with optional filtering and sorting.
   * Each row is a branch-anchor span (AGENT_RUN, WORKFLOW_RUN, TOOL_CALL, etc.) including
   * ones nested under a different root entity. Pairs with {@link getBranch} to expand
   * a single branch into its subtree.
   */
  listBranches(params: ListBranchesArgs = {}): Promise<ListBranchesResponse> {
    return this.observability.listBranches(params);
  }

  /**
   * Retrieves the subtree of spans rooted at a given span. The optional `depth` field
   * bounds descendant levels below the anchor (0 = anchor only; omitted = full subtree).
   */
  getBranch(params: GetBranchArgs): Promise<GetBranchResponse> {
    return this.observability.getBranch(params);
  }

  listScoresBySpan(params: ListScoresBySpanParams): Promise<ListScoresResponse> {
    return this.observability.listScoresBySpan(params);
  }

  /**
   * Deletes traces by ID, cascading to all associated spans and trace-linked
   * signal events (scores, feedback, metrics, logs). Signals without a trace ID
   * are untouched. On ClickHouse-backed stores, reads may briefly return
   * deleted rows until the delete is fully applied.
   */
  deleteTraces(params: { traceIds: string[] }): Promise<{ success: true }> {
    return this.observability.deleteTraces(params);
  }

  /** Scores one or more traces using a specified scorer (fire-and-forget). */
  score(params: {
    scorerName: string;
    targets: Array<{ traceId: string; spanId?: string }>;
  }): Promise<{ status: string; message: string }> {
    return this.observability.score(params);
  }

  // --------------------------------------------------------------------------
  // Logs
  // --------------------------------------------------------------------------

  /** Retrieves a paginated list of observability logs. */
  listLogsVNext(params: ListLogsArgs = {}): Promise<ListLogsResponse> {
    return this.observability.listLogs(params);
  }

  // --------------------------------------------------------------------------
  // Scores
  // --------------------------------------------------------------------------

  /** Retrieves a paginated list of observability scores. */
  listScores(params: ListScoresArgs = {}): Promise<ListScoresResponseNew> {
    return this.observability.listScores(params);
  }

  /** Creates a single score record in the observability store. */
  createScore(params: CreateScoreBody): Promise<CreateScoreResponse> {
    return this.observability.createScore(params);
  }

  /** Deletes score records by scoreId, optionally scoped to a tenant. */
  deleteScores(params: DeleteScoresArgs): Promise<DeleteScoresResponse> {
    return this.observability.deleteScores(params);
  }

  /** Returns an aggregated score value with optional period-over-period comparison. */
  getScoreAggregate(params: GetScoreAggregateArgs): Promise<GetScoreAggregateResponse> {
    return this.observability.getScoreAggregate(params);
  }

  /** Returns score values grouped by specified dimensions. */
  getScoreBreakdown(params: GetScoreBreakdownArgs): Promise<GetScoreBreakdownResponse> {
    return this.observability.getScoreBreakdown(params);
  }

  /** Returns score values bucketed by time interval with optional grouping. */
  getScoreTimeSeries(params: GetScoreTimeSeriesArgs): Promise<GetScoreTimeSeriesResponse> {
    return this.observability.getScoreTimeSeries(params);
  }

  /** Returns percentile values for scores bucketed by time interval. */
  getScorePercentiles(params: GetScorePercentilesArgs): Promise<GetScorePercentilesResponse> {
    return this.observability.getScorePercentiles(params);
  }

  // --------------------------------------------------------------------------
  // Feedback
  // --------------------------------------------------------------------------

  /** Retrieves a paginated list of feedback records. */
  listFeedback(params: ListFeedbackArgs = {}): Promise<ListFeedbackResponse> {
    return this.observability.listFeedback(params);
  }

  /** Creates a single feedback record in the observability store. */
  createFeedback(params: CreateFeedbackBody): Promise<CreateFeedbackResponse> {
    return this.observability.createFeedback(params);
  }

  /** Deletes feedback records by feedbackId, optionally scoped to a tenant. */
  deleteFeedback(params: DeleteFeedbackArgs): Promise<DeleteFeedbackResponse> {
    return this.observability.deleteFeedback(params);
  }

  /** Updates a feedback record's review workflow status. */
  updateFeedbackReviewStatus(params: UpdateFeedbackReviewStatusArgs): Promise<FeedbackRecord> {
    return this.observability.updateFeedbackReviewStatus(params);
  }

  /** Returns an aggregated feedback value with optional period-over-period comparison. */
  getFeedbackAggregate(params: GetFeedbackAggregateArgs): Promise<GetFeedbackAggregateResponse> {
    return this.observability.getFeedbackAggregate(params);
  }

  /** Returns feedback values grouped by specified dimensions. */
  getFeedbackBreakdown(params: GetFeedbackBreakdownArgs): Promise<GetFeedbackBreakdownResponse> {
    return this.observability.getFeedbackBreakdown(params);
  }

  /** Returns feedback values bucketed by time interval with optional grouping. */
  getFeedbackTimeSeries(params: GetFeedbackTimeSeriesArgs): Promise<GetFeedbackTimeSeriesResponse> {
    return this.observability.getFeedbackTimeSeries(params);
  }

  /** Returns percentile values for feedback bucketed by time interval. */
  getFeedbackPercentiles(params: GetFeedbackPercentilesArgs): Promise<GetFeedbackPercentilesResponse> {
    return this.observability.getFeedbackPercentiles(params);
  }

  // --------------------------------------------------------------------------
  // Metrics
  // --------------------------------------------------------------------------

  /** Returns an aggregated metric value with optional period-over-period comparison. */
  getMetricAggregate(params: GetMetricAggregateArgs): Promise<GetMetricAggregateResponse> {
    return this.observability.getMetricAggregate(params);
  }

  /** Returns metric values grouped by specified dimensions. */
  getMetricBreakdown(params: GetMetricBreakdownArgs): Promise<GetMetricBreakdownResponse> {
    return this.observability.getMetricBreakdown(params);
  }

  /** Returns metric values bucketed by time interval with optional grouping. */
  getMetricTimeSeries(params: GetMetricTimeSeriesArgs): Promise<GetMetricTimeSeriesResponse> {
    return this.observability.getMetricTimeSeries(params);
  }

  /** Returns percentile values for a metric bucketed by time interval. */
  getMetricPercentiles(params: GetMetricPercentilesArgs): Promise<GetMetricPercentilesResponse> {
    return this.observability.getMetricPercentiles(params);
  }

  // --------------------------------------------------------------------------
  // Discovery
  // --------------------------------------------------------------------------

  /** Returns distinct metric names with optional prefix filtering. */
  getMetricNames(params: GetMetricNamesArgs = {}): Promise<GetMetricNamesResponse> {
    return this.observability.getMetricNames(params);
  }

  /** Returns distinct label keys for a given metric. */
  getMetricLabelKeys(params: GetMetricLabelKeysArgs): Promise<GetMetricLabelKeysResponse> {
    return this.observability.getMetricLabelKeys(params);
  }

  /** Returns distinct values for a given metric label key. */
  getMetricLabelValues(params: GetMetricLabelValuesArgs): Promise<GetMetricLabelValuesResponse> {
    return this.observability.getMetricLabelValues(params);
  }

  /** Returns distinct entity types from observability data. */
  getEntityTypes(): Promise<GetEntityTypesResponse> {
    return this.observability.getEntityTypes();
  }

  /** Returns distinct entity names with optional type filtering. */
  getEntityNames(params: GetEntityNamesArgs = {}): Promise<GetEntityNamesResponse> {
    return this.observability.getEntityNames(params);
  }

  /** Returns distinct service names from observability data. */
  getServiceNames(): Promise<GetServiceNamesResponse> {
    return this.observability.getServiceNames();
  }

  /** Returns distinct environments from observability data. */
  getEnvironments(): Promise<GetEnvironmentsResponse> {
    return this.observability.getEnvironments();
  }

  /** Returns distinct tags with optional entity type filtering. */
  getTags(params: GetTagsArgs = {}): Promise<GetTagsResponse> {
    return this.observability.getTags(params);
  }

  // ============================================================================
  // Stored Agents
  // ============================================================================

  /**
   * Lists all stored agents with optional pagination
   * @param params - Optional pagination and ordering parameters
   * @returns Promise containing paginated list of stored agents
   */
  public listStoredAgents(params?: ListStoredAgentsParams): Promise<ListStoredAgentsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.perPage !== undefined) {
      searchParams.set('perPage', String(params.perPage));
    }
    if (params?.orderBy) {
      if (params.orderBy.field) {
        searchParams.set('orderBy[field]', params.orderBy.field);
      }
      if (params.orderBy.direction) {
        searchParams.set('orderBy[direction]', params.orderBy.direction);
      }
    }
    if (params?.status) {
      searchParams.set('status', params.status);
    }
    if (params?.authorId) {
      searchParams.set('authorId', params.authorId);
    }
    if (params?.visibility) {
      searchParams.set('visibility', params.visibility);
    }
    if (params?.metadata) {
      searchParams.set('metadata', JSON.stringify(params.metadata));
    }
    if (params?.favoritedOnly) {
      searchParams.set('favoritedOnly', 'true');
    }
    if (params?.pinFavoritedFor) {
      searchParams.set('pinFavoritedFor', params.pinFavoritedFor);
    }

    const queryString = searchParams.toString();
    return this.request(`/stored/agents${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Creates a new stored agent
   * @param params - Agent configuration including id, name, instructions, model, etc.
   * @returns Promise containing the created stored agent
   */
  public createStoredAgent(params: CreateStoredAgentParams): Promise<StoredAgentResponse> {
    return this.request('/stored/agents', {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Gets a stored agent instance by ID for further operations (details, update, delete)
   * @param storedAgentId - ID of the stored agent to retrieve
   * @returns StoredAgent instance
   */
  public getStoredAgent(storedAgentId: string): StoredAgent {
    return new StoredAgent(this.options, storedAgentId);
  }

  // ============================================================================
  // Dynamic Workflows
  // ============================================================================

  /**
   * Lists dynamic workflow definitions, optionally filtered by status or author
   * @param params - Optional filters: `status` ('active' | 'archived') and `authorId`
   * @returns Promise containing the matching definitions and a total count
   */
  public listDynamicWorkflows(params?: ListDynamicWorkflowsParams): Promise<ListDynamicWorkflowsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.authorId) searchParams.set('authorId', params.authorId);

    const queryString = searchParams.toString();
    return this.request(`/stored/workflows${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Creates or replaces a dynamic workflow definition and live-registers it on the server.
   * Optional `dependencies` lets helper workflows referenced by the root definition be
   * saved in the same request; their ids are echoed back as `dependencyIds`.
   * @param params - The workflow definition (id, schemas, graph) plus optional helper dependencies
   * @returns Promise containing the persisted definition and any dependency ids
   */
  public upsertDynamicWorkflow(params: UpsertDynamicWorkflowParams): Promise<UpsertDynamicWorkflowResponse> {
    return this.request('/stored/workflows', {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Gets a dynamic workflow instance by ID for further operations (details, delete).
   * To execute a dynamic workflow, use `getWorkflow(id).createRun()` like any other workflow.
   * @param dynamicWorkflowId - ID of the dynamic workflow definition
   * @returns DynamicWorkflow instance
   */
  public getDynamicWorkflow(dynamicWorkflowId: string): DynamicWorkflow {
    return new DynamicWorkflow(this.options, dynamicWorkflowId);
  }

  /**
   * Retrieves workflow builder settings for UI gating.
   */
  public getWorkflowBuilderSettings(): Promise<WorkflowBuilderSettingsResponse> {
    return this.request('/editor/workflow-builder/settings');
  }

  // ============================================================================
  // Stored Prompt Blocks
  // ============================================================================

  /**
   * Lists all stored prompt blocks with optional pagination
   * @param params - Optional pagination and ordering parameters
   * @returns Promise containing paginated list of stored prompt blocks
   */
  public listStoredPromptBlocks(params?: ListStoredPromptBlocksParams): Promise<ListStoredPromptBlocksResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.perPage !== undefined) {
      searchParams.set('perPage', String(params.perPage));
    }
    if (params?.orderBy) {
      if (params.orderBy.field) {
        searchParams.set('orderBy[field]', params.orderBy.field);
      }
      if (params.orderBy.direction) {
        searchParams.set('orderBy[direction]', params.orderBy.direction);
      }
    }
    if (params?.authorId) {
      searchParams.set('authorId', params.authorId);
    }
    if (params?.status) {
      searchParams.set('status', params.status);
    }
    if (params?.metadata) {
      searchParams.set('metadata', JSON.stringify(params.metadata));
    }

    const queryString = searchParams.toString();
    return this.request(`/stored/prompt-blocks${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Creates a new stored prompt block
   * @param params - Prompt block configuration including name, content, rules, etc.
   * @returns Promise containing the created stored prompt block
   */
  public createStoredPromptBlock(params: CreateStoredPromptBlockParams): Promise<StoredPromptBlockResponse> {
    return this.request('/stored/prompt-blocks', {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Gets a stored prompt block instance by ID for further operations (details, update, delete)
   * @param storedPromptBlockId - ID of the stored prompt block to retrieve
   * @returns StoredPromptBlock instance
   */
  public getStoredPromptBlock(storedPromptBlockId: string): StoredPromptBlock {
    return new StoredPromptBlock(this.options, storedPromptBlockId);
  }

  // ============================================================================
  // Stored Scorer Definitions
  // ============================================================================

  /**
   * Lists all stored scorer definitions with optional pagination
   * @param params - Optional pagination and ordering parameters
   * @returns Promise containing paginated list of stored scorer definitions
   */
  public listStoredScorers(params?: ListStoredScorersParams): Promise<ListStoredScorersResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.perPage !== undefined) {
      searchParams.set('perPage', String(params.perPage));
    }
    if (params?.orderBy) {
      if (params.orderBy.field) {
        searchParams.set('orderBy[field]', params.orderBy.field);
      }
      if (params.orderBy.direction) {
        searchParams.set('orderBy[direction]', params.orderBy.direction);
      }
    }
    if (params?.authorId) {
      searchParams.set('authorId', params.authorId);
    }
    if (params?.metadata) {
      searchParams.set('metadata', JSON.stringify(params.metadata));
    }

    const queryString = searchParams.toString();
    return this.request(`/stored/scorers${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Creates a new stored scorer definition
   * @param params - Scorer definition configuration
   * @returns Promise containing the created stored scorer definition
   */
  public createStoredScorer(params: CreateStoredScorerParams): Promise<StoredScorerResponse> {
    return this.request('/stored/scorers', {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Gets a stored scorer definition instance by ID for further operations (details, update, delete)
   * @param storedScorerId - ID of the stored scorer definition
   * @returns StoredScorer instance
   */
  public getStoredScorer(storedScorerId: string): StoredScorer {
    return new StoredScorer(this.options, storedScorerId);
  }

  // ============================================================================
  // Stored MCP Clients
  // ============================================================================

  /**
   * Lists all stored MCP clients with optional pagination
   * @param params - Optional pagination and ordering parameters
   * @returns Promise containing paginated list of stored MCP clients
   */
  public listStoredMCPClients(params?: ListStoredMCPClientsParams): Promise<ListStoredMCPClientsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.perPage !== undefined) {
      searchParams.set('perPage', String(params.perPage));
    }
    if (params?.orderBy) {
      if (params.orderBy.field) {
        searchParams.set('orderBy[field]', params.orderBy.field);
      }
      if (params.orderBy.direction) {
        searchParams.set('orderBy[direction]', params.orderBy.direction);
      }
    }
    if (params?.authorId) {
      searchParams.set('authorId', params.authorId);
    }
    if (params?.metadata) {
      searchParams.set('metadata', JSON.stringify(params.metadata));
    }

    const queryString = searchParams.toString();
    return this.request(`/stored/mcp-clients${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Creates a new stored MCP client
   * @param params - MCP client configuration
   * @returns Promise containing the created stored MCP client
   */
  public createStoredMCPClient(params: CreateStoredMCPClientParams): Promise<StoredMCPClientResponse> {
    return this.request('/stored/mcp-clients', {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Gets a stored MCP client instance by ID for further operations (details, update, delete)
   * @param storedMCPClientId - ID of the stored MCP client
   * @returns StoredMCPClient instance
   */
  public getStoredMCPClient(storedMCPClientId: string): StoredMCPClient {
    return new StoredMCPClient(this.options, storedMCPClientId);
  }

  // ============================================================================
  // Stored Skills
  // ============================================================================

  /**
   * Lists all stored skills with optional pagination
   * @param params - Optional pagination and ordering parameters
   * @returns Promise containing paginated list of stored skills
   */
  public listStoredSkills(params?: ListStoredSkillsParams): Promise<ListStoredSkillsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.perPage !== undefined) {
      searchParams.set('perPage', String(params.perPage));
    }
    if (params?.orderBy) {
      if (params.orderBy.field) {
        searchParams.set('orderBy[field]', params.orderBy.field);
      }
      if (params.orderBy.direction) {
        searchParams.set('orderBy[direction]', params.orderBy.direction);
      }
    }
    if (params?.authorId) {
      searchParams.set('authorId', params.authorId);
    }
    if (params?.visibility) {
      searchParams.set('visibility', params.visibility);
    }
    if (params?.metadata) {
      searchParams.set('metadata', JSON.stringify(params.metadata));
    }
    if (params?.favoritedOnly) {
      searchParams.set('favoritedOnly', 'true');
    }
    if (params?.pinFavoritedFor) {
      searchParams.set('pinFavoritedFor', params.pinFavoritedFor);
    }

    const queryString = searchParams.toString();
    return this.request(`/stored/skills${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Creates a new stored skill
   * @param params - Skill configuration
   * @returns Promise containing the created stored skill
   */
  public createStoredSkill(params: CreateStoredSkillParams): Promise<StoredSkillResponse> {
    return this.request('/stored/skills', {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Gets a stored skill instance by ID for further operations (details, update, delete)
   * @param storedSkillId - ID of the stored skill
   * @returns StoredSkill instance
   */
  public getStoredSkill(storedSkillId: string): StoredSkill {
    return new StoredSkill(this.options, storedSkillId);
  }

  // ============================================================================
  // Tool Providers
  // ============================================================================

  /**
   * Lists all registered tool providers
   * @returns Promise containing list of tool provider info
   */
  public listToolProviders(): Promise<ListToolProvidersResponse> {
    return this.request('/tool-providers');
  }

  /**
   * Gets a tool provider instance by ID for further operations (listToolkits, listTools, getToolSchema)
   * @param providerId - ID of the tool provider
   * @returns ToolProvider instance
   */
  public getToolProvider(providerId: string): ToolProvider {
    return new ToolProvider(this.options, providerId);
  }

  // ============================================================================
  // Processor Providers
  // ============================================================================

  /**
   * Lists all registered processor providers
   * @returns Promise containing list of processor provider info
   */
  public getProcessorProviders(): Promise<GetProcessorProvidersResponse> {
    return this.request('/processor-providers');
  }

  /**
   * Gets a processor provider instance by ID for further operations
   * @param providerId - ID of the processor provider
   * @returns ProcessorProvider instance
   */
  public getProcessorProvider(providerId: string): ProcessorProvider {
    return new ProcessorProvider(this.options, providerId);
  }

  // ============================================================================
  // System
  // ============================================================================

  /**
   * Retrieves installed Mastra packages and their versions
   * @returns Promise containing the list of installed Mastra packages
   */
  public getSystemPackages(): Promise<GetSystemPackagesResponse> {
    return this.request('/system/packages');
  }

  // ============================================================================
  // Editor / Builder
  // ============================================================================

  /**
   * Retrieves agent builder settings for UI gating.
   * Returns feature flags and configuration set by admin.
   * @returns Promise containing builder settings
   */
  public getBuilderSettings(): Promise<BuilderSettingsResponse> {
    return this.request('/editor/builder/settings');
  }

  /**
   * Retrieves the AI providers/models available under the active builder model
   * policy. The server applies the EE allowlist, so the result can be rendered
   * directly in the model picker.
   * @returns Promise containing the policy-filtered providers/models
   */
  public getBuilderAvailableModels(): Promise<BuilderAvailableModelsResponse> {
    return this.request('/editor/builder/models/available');
  }

  /**
   * Retrieves the authoritative list of valid permission-pattern strings.
   * Used by Studio to validate route→permission literals and gate the sidebar.
   * @returns Promise containing the permission patterns
   */
  public getPermissionPatterns(): Promise<PermissionPatternsResponse> {
    return this.request('/auth/permission-patterns');
  }

  /**
   * Retrieves Agent Builder infrastructure configuration and resolution status.
   * Requires `infrastructure:read` permission.
   * @returns Promise containing infrastructure status
   */
  public getInfrastructureStatus(): Promise<InfrastructureStatusResponse> {
    return this.request('/editor/builder/infrastructure');
  }

  /**
   * Lists known skill registries surfaced by the Agent Builder config.
   * Each entry reports whether the registry is enabled. Disabled or unknown
   * registries return 404 from registry-scoped routes.
   * Requires `stored-skills:read` permission.
   */
  public listBuilderRegistries(): Promise<ListBuilderRegistriesResponse> {
    return this.request('/editor/builder/registries');
  }

  /**
   * Search a builder skill registry. The registry must be enabled or the
   * server returns 404.
   * Requires `stored-skills:read` permission.
   */
  public searchBuilderRegistry(
    registryId: string,
    params: { q: string; limit?: number },
  ): Promise<BuilderRegistrySearchResponse> {
    const search = new URLSearchParams({ q: params.q });
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    return this.request(`/editor/builder/registries/${encodeURIComponent(registryId)}/search?${search.toString()}`);
  }

  /**
   * Fetch the popular skills feed from a builder skill registry.
   * Requires `stored-skills:read` permission.
   */
  public getBuilderRegistryPopular(
    registryId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<BuilderRegistryPopularResponse> {
    const search = new URLSearchParams();
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    if (params?.offset !== undefined) search.set('offset', String(params.offset));
    const query = search.toString();
    return this.request(
      `/editor/builder/registries/${encodeURIComponent(registryId)}/popular${query ? `?${query}` : ''}`,
    );
  }

  /**
   * Fetch the rendered preview content for a single registry skill.
   * Requires `stored-skills:read` permission.
   */
  public getBuilderRegistryPreview(
    registryId: string,
    params: { owner: string; repo: string; path: string },
  ): Promise<BuilderRegistryPreviewResponse> {
    const search = new URLSearchParams({
      owner: params.owner,
      repo: params.repo,
      path: params.path,
    });
    return this.request(`/editor/builder/registries/${encodeURIComponent(registryId)}/preview?${search.toString()}`);
  }

  /**
   * Install a registry skill into the builder's stored-skills DB.
   * Returns 409 when a stored skill with the derived id already exists.
   * Requires `stored-skills:write` permission.
   */
  public installBuilderRegistrySkill(
    registryId: string,
    body: BuilderRegistryInstallBody,
  ): Promise<BuilderRegistryInstallResponse> {
    return this.request(`/editor/builder/registries/${encodeURIComponent(registryId)}/install`, {
      method: 'POST',
      body,
    });
  }

  // ============================================================================
  // Workspace
  // ============================================================================

  /**
   * Lists all workspaces from both Mastra instance and agents
   * @returns Promise containing array of workspace items
   */
  public listWorkspaces(): Promise<ListWorkspacesResponse> {
    return this.request('/workspaces');
  }

  /**
   * Gets the workspace resource for filesystem, search, and skills operations
   * @param workspaceId - Workspace ID to target
   * @returns Workspace instance
   */
  public getWorkspace(workspaceId: string): Workspace {
    return new Workspace(this.options, workspaceId);
  }

  // ============================================================================
  // Stored Workspaces
  // ============================================================================

  /**
   * Lists stored workspace configurations from the database
   * @param params - Optional filter and pagination parameters
   * @returns Promise containing paginated list of stored workspaces
   */
  public listStoredWorkspaces(params?: ListStoredWorkspacesParams): Promise<ListStoredWorkspacesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.perPage !== undefined) searchParams.set('perPage', String(params.perPage));
    if (params?.authorId) searchParams.set('authorId', params.authorId);
    if (params?.orderBy?.field) searchParams.set('orderBy[field]', params.orderBy.field);
    if (params?.orderBy?.direction) searchParams.set('orderBy[direction]', params.orderBy.direction);
    const qs = searchParams.toString();
    return this.request(`/stored/workspaces${qs ? `?${qs}` : ''}`);
  }

  /**
   * Gets a specific stored workspace by ID
   * @param id - The workspace ID
   * @returns Promise containing the stored workspace
   */
  public getStoredWorkspace(id: string): Promise<StoredWorkspaceResponse> {
    return this.request(`/stored/workspaces/${encodeURIComponent(id)}`);
  }

  // ============================================================================
  // Vectors & Embedders
  // ============================================================================

  /**
   * Lists all available vector stores
   * @returns Promise containing list of available vector stores
   */
  public listVectors(): Promise<ListVectorsResponse> {
    return this.request('/vectors');
  }

  /**
   * Lists all available embedding models
   * @returns Promise containing list of available embedders
   */
  public listEmbedders(): Promise<ListEmbeddersResponse> {
    return this.request('/embedders');
  }

  // ============================================================================
  // Datasets
  // ============================================================================

  /**
   * Lists all datasets with optional pagination
   */
  public listDatasets(params?: ListDatasetsParams): Promise<{ datasets: DatasetRecord[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.perPage !== undefined) searchParams.set('perPage', String(params.perPage));
    if (params?.targetType !== undefined) searchParams.set('targetType', params.targetType);
    for (const id of params?.targetIds ?? []) searchParams.append('targetIds', id);
    const qs = searchParams.toString();
    return this.request(`/datasets${qs ? `?${qs}` : ''}`);
  }

  /**
   * Gets a single dataset by ID. Optionally scope the lookup to a specific
   * tenant organization/project — the server returns 404 if the dataset does
   * not belong to the given tenant.
   */
  public getDataset(
    datasetId: string,
    tenancy?: { organizationId?: string; projectId?: string },
  ): Promise<DatasetRecord> {
    const qs = buildTenancyQuery(tenancy);
    return this.request(`/datasets/${encodeURIComponent(datasetId)}${qs}`);
  }

  /**
   * Creates a new dataset
   */
  public createDataset(params: CreateDatasetParams): Promise<DatasetRecord> {
    return this.request('/datasets', { method: 'POST', body: params });
  }

  /**
   * Updates a dataset. Tenancy fields, when provided, scope the existence
   * check on the server side so that a caller can only update datasets that
   * belong to the given tenant.
   */
  public updateDataset(params: UpdateDatasetParams): Promise<DatasetRecord> {
    const { datasetId, organizationId, projectId, ...body } = params;
    const qs = buildTenancyQuery({ organizationId, projectId });
    return this.request(`/datasets/${encodeURIComponent(datasetId)}${qs}`, {
      method: 'PATCH',
      body,
    });
  }

  /**
   * Deletes a dataset. When tenancy fields are supplied, the server only
   * deletes the dataset if it belongs to the given tenant (silent no-op
   * otherwise).
   */
  public deleteDataset(
    datasetId: string,
    tenancy?: { organizationId?: string; projectId?: string },
  ): Promise<{ success: boolean }> {
    const qs = buildTenancyQuery(tenancy);
    return this.request(`/datasets/${encodeURIComponent(datasetId)}${qs}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Dataset Items
  // ============================================================================

  /**
   * Lists items in a dataset with optional pagination, search, and version filter
   */
  public listDatasetItems(
    datasetId: string,
    params?: { page?: number; perPage?: number; search?: string; version?: number | null },
  ): Promise<{ items: DatasetItem[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.perPage !== undefined) searchParams.set('perPage', String(params.perPage));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.version != null) {
      searchParams.set('version', String(params.version));
    }
    const qs = searchParams.toString();
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items${qs ? `?${qs}` : ''}`);
  }

  /**
   * Gets a single dataset item by ID
   */
  public getDatasetItem(datasetId: string, itemId: string): Promise<DatasetItem> {
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}`);
  }

  /**
   * Adds an item to a dataset
   */
  public addDatasetItem(params: AddDatasetItemParams): Promise<DatasetItem> {
    const { datasetId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items`, {
      method: 'POST',
      body,
    });
  }

  /**
   * Updates a dataset item
   */
  public updateDatasetItem(params: UpdateDatasetItemParams): Promise<DatasetItem> {
    const { datasetId, itemId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      body,
    });
  }

  /**
   * Deletes a dataset item
   */
  public deleteDatasetItem(datasetId: string, itemId: string): Promise<{ success: boolean }> {
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Permanently scrubs a dataset item's data from all versions and linked experiment results
   */
  public purgeDatasetItem(
    datasetId: string,
    itemId: string,
    tenancy?: { organizationId?: string; projectId?: string },
  ): Promise<{ success: boolean }> {
    const qs = buildTenancyQuery(tenancy);
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}/purge${qs}`, {
      method: 'DELETE',
    });
  }

  /**
   * Batch inserts items to a dataset
   */
  public batchInsertDatasetItems(
    params: BatchInsertDatasetItemsParams,
  ): Promise<{ items: DatasetItem[]; count: number }> {
    const { datasetId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items/batch`, {
      method: 'POST',
      body,
    });
  }

  /**
   * Batch deletes items from a dataset
   */
  public batchDeleteDatasetItems(
    params: BatchDeleteDatasetItemsParams,
  ): Promise<{ success: boolean; deletedCount: number }> {
    const { datasetId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items/batch`, {
      method: 'DELETE',
      body,
    });
  }

  /**
   * Generates synthetic dataset items using AI. Items are returned for review, not auto-saved.
   */
  public generateDatasetItems(params: GenerateDatasetItemsParams): Promise<{ items: GeneratedItem[] }> {
    const { datasetId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/generate-items`, {
      method: 'POST',
      body,
    });
  }

  /**
   * Cluster experiment failures using AI to identify common failure patterns.
   */
  public clusterFailures(params: {
    modelId: string;
    items: Array<{
      id: string;
      input: unknown;
      output?: unknown;
      error?: string;
      scores?: Record<string, number>;
      existingTags?: string[];
    }>;
    availableTags?: string[];
    prompt?: string;
  }): Promise<{
    clusters: Array<{ id: string; label: string; description: string; itemIds: string[] }>;
    proposedTags?: Array<{ itemId: string; tags: string[]; reason: string }>;
  }> {
    return this.request(`/datasets/cluster-failures`, {
      method: 'POST',
      body: params,
    });
  }

  // ============================================================================
  // Dataset Item Versions
  // ============================================================================

  /**
   * Lists versions for a dataset item
   */
  public getItemHistory(datasetId: string, itemId: string): Promise<{ history: DatasetItemVersionResponse[] }> {
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}/history`);
  }

  /**
   * Gets a specific version of a dataset item
   */
  public getDatasetItemVersion(
    datasetId: string,
    itemId: string,
    datasetVersion: number,
  ): Promise<DatasetItemVersionResponse> {
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}/versions/${datasetVersion}`,
    );
  }

  // ============================================================================
  // Dataset Versions
  // ============================================================================

  /**
   * Lists versions for a dataset
   */
  public listDatasetVersions(
    datasetId: string,
    pagination?: { page?: number; perPage?: number },
  ): Promise<{ versions: DatasetVersionResponse[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (pagination?.page !== undefined) searchParams.set('page', String(pagination.page));
    if (pagination?.perPage !== undefined) searchParams.set('perPage', String(pagination.perPage));
    const qs = searchParams.toString();
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/versions${qs ? `?${qs}` : ''}`);
  }

  // ============================================================================
  // Dataset Experiments
  // ============================================================================

  /**
   * Lists all experiments across all datasets
   */
  public listExperiments(
    params?: ListExperimentsParams,
  ): Promise<{ experiments: DatasetExperiment[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.perPage !== undefined) searchParams.set('perPage', String(params.perPage));
    if (params?.experimentSetId !== undefined) searchParams.set('experimentSetId', params.experimentSetId);
    if (params?.comparisonId !== undefined) searchParams.set('comparisonId', params.comparisonId);
    if (params?.variantId !== undefined) searchParams.set('variantId', params.variantId);
    if (params?.trialIndex !== undefined) searchParams.set('trialIndex', String(params.trialIndex));
    if (params?.targetType !== undefined) searchParams.set('targetType', params.targetType);
    if (params?.targetId !== undefined) searchParams.set('targetId', params.targetId);
    const qs = searchParams.toString();
    return this.request(`/experiments${qs ? `?${qs}` : ''}`);
  }

  /**
   * Gets review status counts aggregated per experiment
   */
  public getExperimentReviewSummary(): Promise<{ counts: ExperimentReviewCounts[] }> {
    return this.request(`/experiments/review-summary`);
  }

  /**
   * Lists experiments for a dataset
   */
  public listDatasetExperiments(
    datasetId: string,
    params?: ListExperimentsParams,
  ): Promise<{ experiments: DatasetExperiment[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.perPage !== undefined) searchParams.set('perPage', String(params.perPage));
    if (params?.experimentSetId !== undefined) searchParams.set('experimentSetId', params.experimentSetId);
    if (params?.comparisonId !== undefined) searchParams.set('comparisonId', params.comparisonId);
    if (params?.variantId !== undefined) searchParams.set('variantId', params.variantId);
    if (params?.trialIndex !== undefined) searchParams.set('trialIndex', String(params.trialIndex));
    if (params?.targetType !== undefined) searchParams.set('targetType', params.targetType);
    if (params?.targetId !== undefined) searchParams.set('targetId', params.targetId);
    const qs = searchParams.toString();
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/experiments${qs ? `?${qs}` : ''}`);
  }

  /**
   * Gets a single dataset experiment by ID
   */
  public getDatasetExperiment(datasetId: string, experimentId: string): Promise<DatasetExperiment> {
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}`);
  }

  /**
   * Deletes a dataset experiment and its results. Tenancy fields, when provided,
   * scope the dataset lookup on the server side.
   *
   * The server also attempts to delete the experiment's observability traces,
   * cascading to their spans and trace-linked signals. Stores without
   * observability or trace deletion support leave the traces in place.
   */
  public deleteDatasetExperiment(
    datasetId: string,
    experimentId: string,
    tenancy?: { organizationId?: string; projectId?: string },
  ): Promise<{ success: boolean }> {
    const qs = buildTenancyQuery(tenancy);
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}${qs}`,
      {
        method: 'DELETE',
      },
    );
  }

  /**
   * Deletes an experiment and its results regardless of dataset association
   * (including experiments orphaned by dataset deletion). When tenancy fields
   * are supplied, the server only deletes the experiment if it belongs to the
   * given tenant (silent no-op otherwise).
   *
   * The server also attempts to delete the experiment's observability traces,
   * cascading to their spans and trace-linked signals. Stores without
   * observability or trace deletion support leave the traces in place.
   */
  public deleteExperiment(
    experimentId: string,
    options?: { organizationId?: string; projectId?: string },
  ): Promise<{ success: boolean }> {
    const qs = buildTenancyQuery(options);
    return this.request(`/experiments/${encodeURIComponent(experimentId)}${qs}`, {
      method: 'DELETE',
    });
  }

  /**
   * Updates a dataset experiment's name, description or metadata
   */
  public updateDatasetExperiment(params: UpdateDatasetExperimentParams): Promise<DatasetExperiment> {
    const { datasetId, experimentId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}`, {
      method: 'PATCH',
      body,
    });
  }

  /**
   * Lists results for a dataset experiment.
   * `tags` restricts the list to results that have all of the given tags.
   */
  public listDatasetExperimentResults(
    datasetId: string,
    experimentId: string,
    options?: { page?: number; perPage?: number; tags?: string[] },
  ): Promise<{ results: DatasetExperimentResult[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (options?.page !== undefined) searchParams.set('page', String(options.page));
    if (options?.perPage !== undefined) searchParams.set('perPage', String(options.perPage));
    for (const tag of options?.tags ?? []) searchParams.append('tags', tag);
    const qs = searchParams.toString();
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/results${qs ? `?${qs}` : ''}`,
    );
  }

  /**
   * Updates an experiment result's status, tags, and/or comment
   */
  public updateDatasetExperimentResult(params: UpdateExperimentResultParams): Promise<DatasetExperimentResult> {
    const { datasetId, experimentId, resultId, ...body } = params;
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/results/${encodeURIComponent(resultId)}`,
      {
        method: 'PATCH',
        body,
      },
    );
  }

  /**
   * Triggers a new dataset experiment
   */
  public triggerDatasetExperiment(params: TriggerDatasetExperimentParams): Promise<{
    experimentId: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    totalItems: number;
    succeededCount: number;
    failedCount: number;
    startedAt: string | Date;
    completedAt: string | Date | null;
    results: Array<{
      itemId: string;
      itemDatasetVersion: number | null;
      input: unknown;
      output: unknown | null;
      groundTruth: unknown | null;
      metadata?: Record<string, unknown> | null;
      error: string | null;
      startedAt: string | Date;
      completedAt: string | Date;
      retryCount: number;
      scores: Array<{
        scorerId: string;
        scorerName: string;
        score: number | null;
        reason: string | null;
        error: string | null;
      }>;
    }>;
  }> {
    const { datasetId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/experiments`, {
      method: 'POST',
      body,
    });
  }

  /**
   * Creates an experiment without starting the in-process runner, so the caller
   * drives the loop (e.g. a Temporal workflow). With a target, execute items
   * server-side via `runExperimentItem`; without one, ingest results via
   * `submitExperimentResult`. Idempotent when a caller-supplied `id` is provided.
   */
  public createDatasetExperiment(params: CreateDatasetExperimentParams): Promise<CreateDatasetExperimentResponse> {
    const { datasetId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/experiments`, {
      method: 'POST',
      body: { ...body, start: false },
    });
  }

  /**
   * Executes the experiment's target against one dataset item server-side,
   * runs the resolved scorers, and upserts the result row keyed by
   * (experimentId, itemId, attempt) — safe to retry.
   */
  public runExperimentItem(params: RunExperimentItemParams): Promise<RunExperimentItemResponse> {
    const { datasetId, experimentId, itemId, ...body } = params;
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/items/${encodeURIComponent(itemId)}/run`,
      {
        method: 'POST',
        body,
      },
    );
  }

  /**
   * Submits (or re-submits) one item result for a target-less (ingestion) experiment.
   * Upsert semantics on (experimentId, itemId, attempt) — safe to retry.
   */
  public submitExperimentResult(params: SubmitExperimentResultParams): Promise<DatasetExperimentResultRow> {
    const { datasetId, experimentId, ...body } = params;
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/results`,
      {
        method: 'POST',
        body,
      },
    );
  }

  /**
   * Finalizes an external experiment. The server computes counts from persisted results. Idempotent.
   */
  public finalizeExperiment(params: FinalizeExperimentParams): Promise<DatasetExperiment> {
    const { datasetId, experimentId } = params;
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/finalize`,
      {
        method: 'POST',
        body: {},
      },
    );
  }

  /**
   * Updates the status, tags, and/or comment on an experiment result
   */
  public updateExperimentResult(params: UpdateExperimentResultParams): Promise<DatasetExperimentResult> {
    const { datasetId, experimentId, resultId, ...body } = params;
    return this.request(
      `/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/results/${encodeURIComponent(resultId)}`,
      {
        method: 'PATCH',
        body,
      },
    );
  }

  /**
   * Compares two dataset experiments for regression detection
   */
  public compareExperiments(params: CompareExperimentsParams): Promise<CompareExperimentsResponse> {
    const { datasetId, ...body } = params;
    return this.request(`/datasets/${encodeURIComponent(datasetId)}/compare`, {
      method: 'POST',
      body,
    });
  }

  // ============================================================================
  // Background Tasks
  // ============================================================================

  /**
   * Lists background tasks with optional filtering and pagination.
   */
  public listBackgroundTasks(params: ListBackgroundTasksParams = {}): Promise<ListBackgroundTasksResponse> {
    const searchParams = new URLSearchParams();
    if (params.agentId) searchParams.set('agentId', params.agentId);
    if (params.status) searchParams.set('status', params.status);
    if (params.runId) searchParams.set('runId', params.runId);
    if (params.threadId) searchParams.set('threadId', params.threadId);
    if (params.resourceId) searchParams.set('resourceId', params.resourceId);
    if (params.toolName) searchParams.set('toolName', params.toolName);
    if (params.toolCallId) searchParams.set('toolCallId', params.toolCallId);
    if (params.fromDate) searchParams.set('fromDate', params.fromDate.toISOString());
    if (params.toDate) searchParams.set('toDate', params.toDate.toISOString());
    if (params.dateFilterBy) searchParams.set('dateFilterBy', params.dateFilterBy);
    if (params.orderBy) searchParams.set('orderBy', params.orderBy);
    if (params.orderDirection) searchParams.set('orderDirection', params.orderDirection);
    if (params.page !== undefined) searchParams.set('page', String(params.page));
    if (params.perPage !== undefined) searchParams.set('perPage', String(params.perPage));
    const qs = searchParams.toString();
    return this.request(`/background-tasks${qs ? `?${qs}` : ''}`);
  }

  /**
   * Gets a single background task by ID.
   */
  public getBackgroundTask(backgroundTaskId: string): Promise<BackgroundTaskResponse> {
    return this.request(`/background-tasks/${encodeURIComponent(backgroundTaskId)}`);
  }

  /**
   * Opens an SSE stream of background task events (completed/failed).
   * Returns a Response that can be consumed as a ReadableStream.
   */
  public async streamBackgroundTasks(params: StreamBackgroundTasksParams = {}) {
    const searchParams = new URLSearchParams();
    if (params.agentId) searchParams.set('agentId', params.agentId);
    if (params.runId) searchParams.set('runId', params.runId);
    if (params.threadId) searchParams.set('threadId', params.threadId);
    if (params.resourceId) searchParams.set('resourceId', params.resourceId);
    if (params.taskId) searchParams.set('taskId', params.taskId);
    const qs = searchParams.toString();
    const response: Response = await this.request(`/background-tasks/stream${qs ? `?${qs}` : ''}`, { stream: true });

    if (!response.ok) {
      throw new Error(`Failed to stream background tasks: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    return response.body.pipeThrough(createSseJsonTransform());
  }

  /**
   * Lists schedules — agent schedules and workflow schedules — with optional
   * filtering by agentId, workflowId, or status. Agent schedules can
   * additionally be filtered by threadId, resourceId, or name.
   */
  public listSchedules(params: ListSchedulesParams = {}): Promise<ListSchedulesResponse> {
    const searchParams = new URLSearchParams();
    if (params.agentId) searchParams.set('agentId', params.agentId);
    if (params.workflowId) searchParams.set('workflowId', params.workflowId);
    if (params.status) searchParams.set('status', params.status);
    if (params.threadId) searchParams.set('threadId', params.threadId);
    if (params.resourceId) searchParams.set('resourceId', params.resourceId);
    if (params.name) searchParams.set('name', params.name);
    const qs = searchParams.toString();
    return this.request(`/schedules${qs ? `?${qs}` : ''}`);
  }

  /**
   * Gets a single schedule by ID.
   */
  public getSchedule(scheduleId: string): Promise<ScheduleResponse> {
    return this.request(`/schedules/${encodeURIComponent(scheduleId)}`);
  }

  /**
   * Creates a schedule. Pass `agentId` (plus `prompt`) to schedule an agent,
   * or `workflowId` (plus optional `inputData`) to schedule a workflow.
   * By default each call creates a new schedule with a random id
   * (`agent_<uuid>` for agents, `schedule_<uuid>` for workflows) — pass `id`
   * to choose a stable id instead; creating one with an id that already
   * exists throws.
   *
   * Trigger (fire) history is read through `listScheduleTriggers(schedule.id)`.
   */
  public createSchedule(options: CreateScheduleInput): Promise<ScheduleResponse> {
    return this.request(`/schedules`, {
      method: 'POST',
      body: options,
    });
  }

  /**
   * Patches an existing schedule. Fields apply to the matching target type;
   * agent-only fields on a workflow schedule are rejected. `threadId` /
   * `resourceId` are immutable — to retarget, delete and recreate.
   */
  public updateSchedule(scheduleId: string, patch: UpdateScheduleInput): Promise<ScheduleResponse> {
    return this.request(`/schedules/${encodeURIComponent(scheduleId)}`, {
      method: 'PATCH',
      body: patch,
    });
  }

  /**
   * Deletes a schedule.
   */
  public deleteSchedule(scheduleId: string): Promise<{ message: string }> {
    return this.request(`/schedules/${encodeURIComponent(scheduleId)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Fires a schedule manually, out-of-band from the cron schedule. Behaves
   * like a scheduled fire but does not advance `nextFireAt`. The returned
   * `claimId` is the trigger row's runId — look it up via
   * `listScheduleTriggers(scheduleId)`.
   */
  public runSchedule(scheduleId: string): Promise<RunScheduleResponse> {
    return this.request(`/schedules/${encodeURIComponent(scheduleId)}/run`, {
      method: 'POST',
    });
  }

  /**
   * Lists trigger history for a schedule, ordered by actualFireAt descending.
   */
  public listScheduleTriggers(
    scheduleId: string,
    params: ListScheduleTriggersParams = {},
  ): Promise<ListScheduleTriggersResponse> {
    const searchParams = new URLSearchParams();
    if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
    if (params.fromActualFireAt !== undefined) searchParams.set('fromActualFireAt', String(params.fromActualFireAt));
    if (params.toActualFireAt !== undefined) searchParams.set('toActualFireAt', String(params.toActualFireAt));
    const qs = searchParams.toString();
    return this.request(`/schedules/${encodeURIComponent(scheduleId)}/triggers${qs ? `?${qs}` : ''}`);
  }

  /**
   * Pauses a schedule. The scheduler tick loop will skip paused schedules.
   * Idempotent — pausing an already-paused schedule returns the current state unchanged.
   * Pause status survives redeploys.
   */
  public pauseSchedule(scheduleId: string): Promise<ScheduleResponse> {
    return this.request(`/schedules/${encodeURIComponent(scheduleId)}/pause`, { method: 'POST' });
  }

  /**
   * Resumes a paused schedule. Recomputes nextFireAt from "now" so a long-paused schedule
   * does not fire a backlog. Idempotent — resuming an already-active schedule returns
   * the current state unchanged.
   */
  public resumeSchedule(scheduleId: string): Promise<ScheduleResponse> {
    return this.request(`/schedules/${encodeURIComponent(scheduleId)}/resume`, { method: 'POST' });
  }
}
